import React, { useState, useEffect, useMemo } from 'react';
import { manifestAPI } from '../../utils/api';

/**
 * Manifest curation page.
 *
 * Two modes:
 *   1. Browse tab — read-only sectioned view of every tile being served,
 *      with a locale preview dropdown so the curator can see what en-GB,
 *      en-US, ja-JP etc. would receive.
 *   2. Edit tab — JSON editor (textarea) with validate / save / rollback.
 *      Save calls PUT /api/admin/manifest which atomically replaces
 *      data/manifest.json on the Lightsail server.
 *
 * The mostly-read curator will live in Browse; Edit is for power-users
 * who want to add/remove tiles in bulk. A full tree form-builder would
 * triple the page size for marginal benefit; the JSON shape is stable.
 */
export default function ContentCurationPage() {
    const [meta, setMeta] = useState(null);
    const [manifestText, setManifestText] = useState('');
    const [originalText, setOriginalText] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [savedToast, setSavedToast] = useState(null);

    const [tab, setTab] = useState('browse');
    const [previewLocale, setPreviewLocale] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const load = () => {
        setLoading(true);
        setError(null);
        manifestAPI.get()
            .then(res => {
                const pretty = JSON.stringify(res.data.manifest, null, 2);
                setManifestText(pretty);
                setOriginalText(pretty);
                setMeta(res.data.meta);
            })
            .catch(err => setError(err.response?.data?.message || err.message))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const parsed = useMemo(() => {
        try { return JSON.parse(manifestText); }
        catch { return null; }
    }, [manifestText]);

    const dirty = manifestText !== originalText;

    const onSave = async () => {
        if (!parsed) {
            setError('JSON is invalid — fix syntax before saving.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const r = await manifestAPI.put(parsed);
            setSavedToast(`Saved · ${r.data.rows} rows · ${r.data.tiles} tiles · ${r.data.bytes} bytes`);
            setOriginalText(manifestText);
            // Refresh meta so the "Last updated" line reflects the new mtime.
            setTimeout(() => { load(); setSavedToast(null); }, 1500);
        } catch (err) {
            setError(err.response?.data?.message || err.response?.data?.error || err.message);
        } finally {
            setSaving(false);
        }
    };

    const onRollback = async () => {
        if (!window.confirm('Restore manifest from the .bak backup? Your unsaved JSON edits will be lost.')) return;
        try {
            await manifestAPI.rollback();
            load();
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        }
    };

    if (loading) return <div className="text-gray-400">Loading manifest…</div>;

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Manifest curation</h2>
                    {meta && (
                        <p className="text-xs text-gray-500 mt-1">
                            Last updated {new Date(meta.mtime).toLocaleString()} · {meta.bytes} bytes
                        </p>
                    )}
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={onRollback}
                        className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">
                        Roll back to .bak
                    </button>
                    {tab === 'edit' && (
                        <button
                            onClick={onSave}
                            disabled={!dirty || !parsed || saving}
                            className={`px-4 py-2 text-sm rounded text-white ${
                                !dirty || !parsed || saving
                                    ? 'bg-gray-300 cursor-not-allowed'
                                    : 'bg-zebcast-purple hover:opacity-90'
                            }`}>
                            {saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
                        </button>
                    )}
                </div>
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded text-sm">
                    {error}
                </div>
            )}
            {savedToast && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-800 rounded text-sm">
                    {savedToast}
                </div>
            )}

            <div className="border-b border-gray-200 mb-4">
                <nav className="flex gap-4">
                    {['browse', 'edit'].map(t => (
                        <button
                            key={t}
                            onClick={() => setTab(t)}
                            className={`pb-2 text-sm font-medium capitalize ${
                                tab === t
                                    ? 'border-b-2 border-zebcast-purple text-zebcast-purple'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}>
                            {t}
                        </button>
                    ))}
                </nav>
            </div>

            {tab === 'browse' && parsed && (
                <BrowseView manifest={parsed}
                            previewLocale={previewLocale}
                            onLocaleChange={setPreviewLocale}
                            searchQuery={searchQuery}
                            onSearchChange={setSearchQuery} />
            )}

            {tab === 'edit' && (
                <EditView text={manifestText}
                          onChange={setManifestText}
                          parsed={parsed} />
            )}
        </div>
    );
}

// ─── Browse tab ───────────────────────────────────────────────────────────

function BrowseView({ manifest, previewLocale, onLocaleChange, searchQuery, onSearchChange }) {
    const filtered = useMemo(
        () => filterManifestForLocale(manifest, previewLocale),
        [manifest, previewLocale]
    );
    const q = searchQuery.trim().toLowerCase();

    let totalTiles = 0;
    for (const r of filtered.rows || []) {
        for (const s of (r.sections || [])) totalTiles += (s.tiles || []).length;
        if (Array.isArray(r.tiles)) totalTiles += r.tiles.length;
    }

    return (
        <div>
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <label className="text-sm text-gray-700">
                    Preview locale:
                    <select value={previewLocale}
                            onChange={e => onLocaleChange(e.target.value)}
                            className="ml-2 border border-gray-300 rounded px-2 py-1 text-sm">
                        <option value="">(no filter — show all)</option>
                        <option value="en-GB">en-GB · United Kingdom</option>
                        <option value="en-US">en-US · United States</option>
                        <option value="en-AU">en-AU · Australia</option>
                        <option value="en">en · any English</option>
                        <option value="fr-FR">fr-FR · France</option>
                        <option value="de-DE">de-DE · Germany</option>
                        <option value="ja-JP">ja-JP · Japan</option>
                    </select>
                </label>
                <input
                    type="text"
                    placeholder="Search tile titles / URLs / tags…"
                    value={searchQuery}
                    onChange={e => onSearchChange(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1 text-sm flex-1 min-w-[200px]"
                />
                <span className="text-xs text-gray-500">
                    {filtered.rows?.length || 0} rows · {totalTiles} tiles
                </span>
            </div>

            <div className="space-y-4">
                {(filtered.rows || []).map(row => (
                    <RowCard key={row.id} row={row} q={q} />
                ))}
            </div>
        </div>
    );
}

function RowCard({ row, q }) {
    const sections = row.sections && row.sections.length
        ? row.sections
        : (row.tiles ? [{ id: '_flat', title: '', tiles: row.tiles }] : []);

    const visibleSections = sections.map(s => ({
        ...s,
        tiles: (s.tiles || []).filter(t => matchesQuery(t, q)),
    })).filter(s => s.tiles.length > 0 || !q);

    if (q && visibleSections.every(s => s.tiles.length === 0)) return null;

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex justify-between items-center">
                    <div>
                        <h3 className="text-base font-semibold text-gray-900">{row.title}</h3>
                        <p className="text-xs text-gray-500">id: {row.id} · type: {row.type || '(default)'}</p>
                    </div>
                    <span className="text-xs text-gray-500">
                        {sections.reduce((n, s) => n + (s.tiles || []).length, 0)} tiles
                    </span>
                </div>
            </div>
            <div className="p-4 space-y-4">
                {visibleSections.map(s => (
                    <div key={s.id || s.title}>
                        {s.title && (
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                {s.title}
                            </h4>
                        )}
                        <table className="min-w-full text-sm">
                            <tbody className="divide-y divide-gray-100">
                                {s.tiles.map(t => (
                                    <tr key={t.id} className="hover:bg-gray-50">
                                        <td className="px-2 py-2 w-1/4 font-medium text-gray-900">{t.title}</td>
                                        <td className="px-2 py-2 w-2/4 text-blue-700 break-all">
                                            <a href={t.deeplink_url} target="_blank" rel="noreferrer"
                                               className="hover:underline">
                                                {t.deeplink_url}
                                            </a>
                                        </td>
                                        <td className="px-2 py-2 w-1/4 text-xs text-gray-500">
                                            {t.path_hint && (
                                                <span className={`inline-block px-1.5 py-0.5 rounded text-xs mr-1 ${
                                                    t.path_hint === 'direct_hls' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                                                }`}>{t.path_hint}</span>
                                            )}
                                            {t.epg_channel_id && (
                                                <span className="inline-block px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-xs mr-1"
                                                      title={`EPG channel id: ${t.epg_channel_id}`}>
                                                    📺 {t.epg_channel_id}
                                                </span>
                                            )}
                                            {(t.tags || []).slice(0, 3).map(tag => (
                                                <span key={tag} className="inline-block px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-xs mr-1">
                                                    {tag}
                                                </span>
                                            ))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>
        </div>
    );
}

function matchesQuery(tile, q) {
    if (!q) return true;
    const blob = [
        tile.title, tile.deeplink_url, tile.fallback_url, tile.id,
        ...(tile.tags || [])
    ].filter(Boolean).join(' ').toLowerCase();
    return blob.includes(q);
}

// Mirror of the server-side locale filter. Keeps the preview honest.
function filterManifestForLocale(manifest, requestLocale) {
    if (!requestLocale) return manifest;
    const matches = (list, locale) => {
        if (!Array.isArray(list) || list.length === 0) return true;
        const r = locale.toLowerCase();
        return list.some(c => {
            if (typeof c !== 'string') return false;
            const cl = c.toLowerCase();
            return r === cl || r.startsWith(cl + '-') || cl.startsWith(r + '-');
        });
    };
    const out = { ...manifest, rows: [] };
    for (const row of manifest.rows || []) {
        const fr = { ...row };
        if (Array.isArray(row.sections)) {
            fr.sections = row.sections
                .filter(s => matches(s.locale, requestLocale))
                .map(s => ({ ...s, tiles: (s.tiles || []).filter(t => matches(t.locale, requestLocale)) }))
                .filter(s => (s.tiles || []).length > 0);
        }
        if (Array.isArray(row.tiles)) {
            fr.tiles = row.tiles.filter(t => matches(t.locale, requestLocale));
        }
        const hasContent = (fr.sections && fr.sections.length) || (fr.tiles && fr.tiles.length);
        if (hasContent || (!row.sections && !row.tiles)) out.rows.push(fr);
    }
    return out;
}

// ─── Edit tab ─────────────────────────────────────────────────────────────

function EditView({ text, onChange, parsed }) {
    const valid = parsed !== null;
    return (
        <div>
            <div className="mb-2 text-xs text-gray-500">
                {valid
                    ? `JSON valid · ${(parsed.rows || []).length} rows`
                    : <span className="text-red-600 font-medium">JSON has a syntax error.</span>}
            </div>
            <textarea
                value={text}
                onChange={e => onChange(e.target.value)}
                spellCheck={false}
                className="w-full font-mono text-xs border border-gray-300 rounded p-3"
                style={{ height: '70vh', tabSize: 2 }}
            />
            <p className="mt-2 text-xs text-gray-500">
                Save calls <code>PUT /api/admin/manifest</code> which atomically replaces{' '}
                <code>data/manifest.json</code> on the Lightsail server. The previous version
                is kept at <code>data/manifest.json.bak</code> for rollback.
            </p>
        </div>
    );
}
